import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { RefreshCw, LogOut, Star, AlertTriangle, Search, CheckCircle2, BedDouble } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import { useStayCheckedOut } from "@/hooks/useStayCheckedOut";

function DepartureTable({ items, navigate, requestCheckout, testid }) {
  return (
    <Card className="overflow-hidden">
      <table className="w-full" data-testid={testid}>
        <thead className="bg-hocp-bgCard border-b border-hocp-border">
          <tr>{["Code", "Guest", "Room", "Expected Out", "Balance", "Status", "Alerts", "Actions"].map(h => (
            <th key={h} className="px-4 py-2.5 text-[10px] uppercase tracking-[0.1em] text-hocp-textMuted mono text-left">{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {items.map(i => {
            const isVip = i.guest_vip && i.guest_vip !== "none";
            const owes = i.status !== "checked_out" && i.outstanding_balance > 0.01;
            return (
              <tr key={i.id} data-testid={`departure-row-${i.code}`}
                className={`border-b border-hocp-border last:border-0 hover:bg-hocp-bgSubtle ${owes ? "bg-red-500/5" : ""}`}>
                <td className="px-4 py-2 mono text-hocp-textPrimary">{i.code}</td>
                <td className="px-4 py-2 text-hocp-textPrimary flex items-center gap-1">
                  {isVip && <Star className="w-3 h-3 text-amber-400 fill-amber-400"/>}
                  {i.guest_name}
                </td>
                <td className="px-4 py-2 mono text-hocp-textPrimary">{i.room_number || "—"}</td>
                <td className="px-4 py-2 mono text-[12px] text-hocp-textMuted">{(i.expected_departure || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-4 py-2 mono text-[12px]">
                  {i.outstanding_balance > 0.01
                    ? <span className={`inline-flex items-center gap-1 ${owes ? "text-red-300 font-semibold" : "text-amber-300"}`} data-testid={`departure-balance-${i.code}`}>
                        {owes && <AlertTriangle className="w-3 h-3"/>}${i.outstanding_balance.toFixed(2)}
                      </span>
                    : <span className="text-hocp-textMuted">$0</span>}
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-1.5">
                    <Badge tone={i.status === "checked_out" ? "success" : "warning"}>{i.status}</Badge>
                    {owes && <span className="text-[9px] mono uppercase px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/40" data-testid={`departure-balance-badge-${i.code}`}>Balance Due</span>}
                  </div>
                </td>
                <td className="px-4 py-2">
                  <div className="flex flex-wrap gap-1">
                    {(i.alerts || []).map((a, idx) => (
                      <span key={idx} className="text-[10px] mono uppercase px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30 flex items-center gap-1">
                        <AlertTriangle className="w-2.5 h-2.5"/>{a}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-2 space-x-1">
                  <Button size="sm" variant="outline" onClick={() => navigate(`/pms/stays/${i.id}`)}>View</Button>
                  {i.status !== "checked_out" && !i.checkout_requested_at && (
                    <Button size="sm" onClick={() => requestCheckout(i)} data-testid={`request-checkout-${i.code}`}>
                      <LogOut className="w-3 h-3 mr-1"/>Request
                    </Button>
                  )}
                  {i.status !== "checked_out" && (
                    <Button
                      size="sm"
                      variant="default"
                      onClick={async () => {
                        try {
                          const existing = await import("@/lib/api").then(m => m.default.get(`/checkout/by-stay/${i.id}`));
                          if (existing.data && existing.data.id) {
                            navigate(`/checkout/${existing.data.id}`);
                            return;
                          }
                          const r = await import("@/lib/api").then(m => m.default.post(`/checkout/initiate`, {
                            stay_id: i.id, channel: "web",
                          }));
                          navigate(`/checkout/${r.data.id}`);
                        } catch (e) {
                          const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message;
                          const { toast } = await import("sonner");
                          toast.error(String(msg));
                        }
                      }}
                      data-testid={`start-checkout-${i.code}`}
                    >
                      <LogOut className="w-3 h-3 mr-1"/>Checkout
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
          {!items.length && <tr><td colSpan={8} className="text-center py-8 text-hocp-textMuted text-[13px]">None in this group</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

function KpiCard({ label, value, tone = "neutral" }) {
  const tones = {
    neutral: "border-hocp-border bg-hocp-bgSubtle",
    success: "border-emerald-500/30 bg-emerald-500/5",
    warning: "border-amber-500/30 bg-amber-500/5",
    info: "border-blue-500/30 bg-blue-500/5",
    error: "border-red-500/30 bg-red-500/5",
  };
  return (
    <div data-testid={`departure-kpi-${(label || "").toLowerCase().replace(/\s+/g, "-")}`} className={`p-3 rounded-md border ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-[0.15em] text-hocp-textMuted mono mb-1.5">{label}</div>
      <div className="font-mono text-xl text-white tabular-nums">{value ?? 0}</div>
    </div>
  );
}

export default function DepartureBoard() {
  const [data, setData] = useState({ items: [], kpis: {} });
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/pms/reservations/board/departures?date=${date}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, [date]);

  useEffect(() => { load(); }, [load]);
  useStayCheckedOut(load);
  useEffect(() => { const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const requestCheckout = async (item) => {
    try {
      await api.post(`/pms/stays/${item.id}/request-checkout`);
      toast.success("Checkout requested");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  let items = data.items;
  if (q) items = items.filter(i => (i.guest_name || "").toLowerCase().includes(q.toLowerCase()));

  const inHouse = items.filter(i => i.status !== "checked_out");
  const checkedOut = items.filter(i => i.status === "checked_out");
  const owingCount = inHouse.filter(i => i.outstanding_balance > 0.01).length;

  return (
    <div className="space-y-6" data-testid="departure-board-page">
      <PageHeader
        title="Departure Board"
        subtitle={`${date} · ${inHouse.length} still in-house · ${checkedOut.length} checked out`}
        action={
          <div className="flex items-center gap-2">
            <DatePicker value={(date || '').slice(0,10)} onChange={(_v) => setDate(_v)} data-testid="dep-board-date"/>
            <Button variant="outline" onClick={load} data-testid="dep-board-refresh"><RefreshCw className="w-4 h-4 mr-2"/>Refresh</Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="departure-kpis">
        <KpiCard label="Today's Departures" value={data.kpis?.todays_departures} tone="neutral"/>
        <KpiCard label="Checked Out" value={data.kpis?.checked_out} tone="success"/>
        <KpiCard label="Pending Checkout" value={data.kpis?.pending_checkout} tone="warning"/>
        <KpiCard label="Late Checkout" value={data.kpis?.late_checkout} tone="error"/>
        <KpiCard label="With Balance" value={data.kpis?.with_outstanding_balance} tone="warning"/>
      </div>

      <Card className="p-3 flex items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-hocp-textMuted"/>
          <Input placeholder="Search guest" aria-label="Search guests" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="departure-search"/>
        </div>
        <span className="text-[11px] mono text-hocp-textMuted">{items.length} shown {loading && "· loading…"}</span>
      </Card>

      <div className="space-y-6">
        <section data-testid="departure-section-inhouse">
          <div className="flex items-center gap-2 mb-3">
            <BedDouble className="w-4 h-4 text-amber-400" />
            <h2 className="text-[13px] font-semibold uppercase tracking-wider text-amber-300">Still In-House</h2>
            <span className="text-[11px] mono px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30" data-testid="departure-count-inhouse">{inHouse.length}</span>
            {owingCount > 0 && (
              <span className="text-[11px] mono px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/40 flex items-center gap-1" data-testid="departure-owing-count">
                <AlertTriangle className="w-3 h-3"/>{owingCount} owe balance
              </span>
            )}
          </div>
          <DepartureTable items={inHouse} navigate={navigate} requestCheckout={requestCheckout} testid="departure-table-inhouse"/>
        </section>

        <section data-testid="departure-section-checkedout">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <h2 className="text-[13px] font-semibold uppercase tracking-wider text-emerald-300">Checked Out</h2>
            <span className="text-[11px] mono px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" data-testid="departure-count-checkedout">{checkedOut.length}</span>
          </div>
          <DepartureTable items={checkedOut} navigate={navigate} requestCheckout={requestCheckout} testid="departure-table-checkedout"/>
        </section>
      </div>
    </div>
  );
}
